/** Canvas-drawn shell backdrop (base + two radial layers) matching legacy RouteShell CSS. */

export type AmbientRgba = {
  r: number
  g: number
  b: number
  a: number
}

export type AmbientRgb = {
  r: number
  g: number
  b: number
}

export type AmbientRadialStop = {
  /** Stop position along the radial gradient ray (0–1). */
  offset: number
  color: AmbientRgba
}

export type AmbientRadialGlowPhysics = {
  periodMs: number
  /** Matches CSS `animation-direction: reverse`. */
  reversePhase: boolean
  translateStartXPct: number
  translateStartYPct: number
  translateEndXPct: number
  translateEndYPct: number
  scaleStart: number
  scaleEnd: number
  opacityMin: number
  opacityMax: number
}

export type AmbientRadialLayerConfig = {
  centerXPct: number
  centerYPct: number
  stops: AmbientRadialStop[]
  glow: AmbientRadialGlowPhysics
}

export type AmbientBeamGradientStop = {
  /** Position along the beam axis (0 = crack, 1 = foot). */
  offset: number
  color: AmbientRgba
}

/**
 * Soft “ceiling crack” light shaft: trapezoid filled with an axial linear gradient,
 * animated with the same easing rhythm as radial shell glows.
 */
export type AmbientLightBeamConfig = {
  /** Lower values draw first (read as farther / deeper). */
  depthOrder: number
  /** Horizontal offset of the crack from viewport center (px); same reference as {@link AMBIENT_BEAM_WIDTH_REFERENCE_PX} spacing. */
  crackXOffsetFromCenterPx: number
  /** Crack depth from top of the canvas as percent of height (0–100). */
  crackYPct: number
  /** Degrees from vertical; positive tilts the foot of the beam toward +x. */
  angleDeg: number
  /** Half-width at the crack: `(pct/100) * reference width` px (see {@link AMBIENT_BEAM_WIDTH_REFERENCE_PX}). */
  halfWidthCrackPct: number
  /** Half-width at the beam foot: same reference scaling as the crack. */
  halfWidthFootPct: number
  /** Beam length as a percent of viewport height (may exceed 100 for overscan). */
  reachPct: number
  stops: AmbientBeamGradientStop[]
  glow: AmbientRadialGlowPhysics
  /** Scales peak alpha after the breathing curve (layered depth). */
  depthOpacity: number
  /** Edge softness: Canvas `filter: blur(...)` in CSS px (canvas coordinate space). */
  edgeBlurPx: number
}

export type AmbientShellBackgroundConfig = {
  baseTop: AmbientRgb
  baseBottom: AmbientRgb
  radialBefore: AmbientRadialLayerConfig
  radialAfter: AmbientRadialLayerConfig
  /** Volumetric-style shafts drawn after radials; empty to disable. */
  lightBeams: AmbientLightBeamConfig[]
}

/** Merge patch: keep base + radials, omit light shafts (non-`home` ambient presets). */
export const AMBIENT_SHELL_NO_LIGHT_BEAMS: Partial<AmbientShellBackgroundConfig> = {
  lightBeams: [],
}

const rgb = (r: number, g: number, b: number): AmbientRgb => ({ r, g, b })

const rgba = (r: number, g: number, b: number, a: number): AmbientRgba => ({
  r,
  g,
  b,
  a,
})

const BEAM_WOBBLE_X_PCT = 0.22
const BEAM_WOBBLE_Y_PCT = 0.18
const BEAM_SCALE_LO = 0.994
const BEAM_SCALE_HI = 1.006

/** Percents in {@link AmbientLightBeamConfig.halfWidthCrackPct} / `halfWidthFootPct` resolve against this width so shafts match an ~800px layout on wider canvases. */
export const AMBIENT_BEAM_WIDTH_REFERENCE_PX = 800

function beamHalfWidthCrackPx(beam: AmbientLightBeamConfig): number {
  return (beam.halfWidthCrackPct / 100) * AMBIENT_BEAM_WIDTH_REFERENCE_PX
}

function beamHalfWidthFootPx(beam: AmbientLightBeamConfig): number {
  return (beam.halfWidthFootPct / 100) * AMBIENT_BEAM_WIDTH_REFERENCE_PX
}

function caveShaftGlow(
  periodMs: number,
  reversePhase: boolean,
  opacityMin: number,
  opacityMax: number,
  wobble?: { xPct?: number; yPct?: number },
): AmbientRadialGlowPhysics {
  const wx = wobble?.xPct ?? BEAM_WOBBLE_X_PCT
  const wy = wobble?.yPct ?? BEAM_WOBBLE_Y_PCT
  return {
    opacityMax,
    opacityMin,
    periodMs,
    reversePhase,
    scaleEnd: BEAM_SCALE_HI,
    scaleStart: BEAM_SCALE_LO,
    translateEndXPct: wx,
    translateEndYPct: wy,
    translateStartXPct: -wx,
    translateStartYPct: -wy,
  }
}

const WARM_HOME_SHAFT_STOPS: AmbientBeamGradientStop[] = [
  { color: rgba(255, 246, 220, 0.1), offset: 0 },
  { color: rgba(255, 230, 198, 0.078), offset: 0.22 },
  { color: rgba(255, 218, 178, 0.026), offset: 0.34 },
  { color: rgba(255, 205, 165, 0.005), offset: 0.41 },
  { color: rgba(0, 0, 0, 0), offset: 0.5 },
]

const ACTIVE_SHAFT_STOPS: AmbientBeamGradientStop[] = [
  { color: rgba(255, 248, 225, 0.12), offset: 0 },
  { color: rgba(255, 233, 205, 0.092), offset: 0.22 },
  { color: rgba(255, 215, 175, 0.03), offset: 0.34 },
  { color: rgba(255, 200, 160, 0.006), offset: 0.41 },
  { color: rgba(0, 0, 0, 0), offset: 0.5 },
]

const GOLD_SHAFT_STOPS: AmbientBeamGradientStop[] = [
  { color: rgba(255, 240, 190, 0.12), offset: 0 },
  { color: rgba(255, 225, 165, 0.09), offset: 0.23 },
  { color: rgba(248, 200, 130, 0.028), offset: 0.35 },
  { color: rgba(235, 185, 110, 0.005), offset: 0.42 },
  { color: rgba(0, 0, 0, 0), offset: 0.52 },
]

const PRESSURE_SHAFT_STOPS: AmbientBeamGradientStop[] = [
  { color: rgba(255, 205, 175, 0.088), offset: 0 },
  { color: rgba(228, 130, 108, 0.065), offset: 0.23 },
  { color: rgba(190, 95, 82, 0.022), offset: 0.35 },
  { color: rgba(150, 72, 64, 0.004), offset: 0.42 },
  { color: rgba(0, 0, 0, 0), offset: 0.52 },
]

const END_SHAFT_STOPS: AmbientBeamGradientStop[] = [
  { color: rgba(130, 88, 78, 0.055), offset: 0 },
  { color: rgba(95, 62, 56, 0.032), offset: 0.24 },
  { color: rgba(65, 42, 38, 0.009), offset: 0.36 },
  { color: rgba(0, 0, 0, 0), offset: 0.46 },
]

function defaultTripleCaveBeams(stops: AmbientBeamGradientStop[]): AmbientLightBeamConfig[] {
  /** Overlapping cluster with spread in placement, width, and breathing opacity ranges. */
  return [
    {
      angleDeg: -1.15,
      crackXOffsetFromCenterPx: -39,
      crackYPct: 1.7,
      depthOpacity: 0.34,
      depthOrder: 0,
      edgeBlurPx: 7,
      glow: caveShaftGlow(24_000, true, 0.45, 0.86, { xPct: 0.32, yPct: 0.26 }),
      halfWidthCrackPct: 0.065,
      halfWidthFootPct: 8.5,
      reachPct: 97,
      stops,
    },
    {
      angleDeg: 0.5,
      crackXOffsetFromCenterPx: 5,
      crackYPct: 3.6,
      depthOpacity: 0.67,
      depthOrder: 1,
      edgeBlurPx: 11,
      glow: caveShaftGlow(17_200, false, 0.52, 1, { xPct: 0.18, yPct: 0.34 }),
      halfWidthCrackPct: 0.118,
      halfWidthFootPct: 19,
      reachPct: 130,
      stops,
    },
    {
      angleDeg: -0.4,
      crackXOffsetFromCenterPx: 35,
      crackYPct: 2.05,
      depthOpacity: 0.89,
      depthOrder: 2,
      edgeBlurPx: 9,
      glow: caveShaftGlow(33_500, true, 0.58, 0.93, { xPct: 0.38, yPct: 0.2 }),
      halfWidthCrackPct: 0.086,
      halfWidthFootPct: 13.5,
      reachPct: 110,
      stops,
    },
  ]
}

/** Standard shell (no `.gold` class): RouteShell.module.css defaults */
export const DEFAULT_AMBIENT_SHELL_BACKGROUND: AmbientShellBackgroundConfig = {
  baseBottom: rgb(0x12, 0x0e, 0x0c),
  baseTop: rgb(9, 8, 7),
  radialAfter: {
    centerXPct: 82,
    centerYPct: 18,
    glow: {
      opacityMax: 1,
      opacityMin: 0.88,
      periodMs: 20_000,
      reversePhase: true,
      scaleEnd: 1,
      scaleStart: 1.04,
      translateEndXPct: -10,
      translateEndYPct: -5,
      translateStartXPct: 5,
      translateStartYPct: 3,
    },
    stops: [
      { color: rgba(90, 190, 255, 0.18), offset: 0 },
      { color: rgba(55, 140, 235, 0.05), offset: 0.22 },
      { color: rgba(0, 0, 0, 0), offset: 0.29 },
    ],
  },
  radialBefore: {
    centerXPct: 28,
    centerYPct: 12,
    glow: {
      opacityMax: 1,
      opacityMin: 0.92,
      periodMs: 14_000,
      reversePhase: false,
      scaleEnd: 1.07,
      scaleStart: 1,
      translateEndXPct: 6,
      translateEndYPct: 5,
      translateStartXPct: -4,
      translateStartYPct: -2,
    },
    stops: [
      { color: rgba(255, 214, 120, 0.2), offset: 0 },
      { color: rgba(240, 180, 80, 0.05), offset: 0.22 },
      { color: rgba(0, 0, 0, 0), offset: 0.36 },
    ],
  },
  lightBeams: defaultTripleCaveBeams(WARM_HOME_SHAFT_STOPS),
}

/** Brighter warm base, expanded radials; top-right glow reads yellow–orange instead of blue. */
export const ACTIVE_AMBIENT_SHELL_BACKGROUND: AmbientShellBackgroundConfig = {
  baseBottom: rgb(0x18, 0x14, 0x0e),
  baseTop: rgb(15, 13, 10),
  radialAfter: {
    centerXPct: 82,
    centerYPct: 18,
    glow: {
      opacityMax: 1,
      opacityMin: 0.88,
      periodMs: 20_000,
      reversePhase: true,
      scaleEnd: 0.99,
      scaleStart: 1.06,
      translateEndXPct: -10,
      translateEndYPct: -5,
      translateStartXPct: 5,
      translateStartYPct: 3,
    },
    stops: [
      { color: rgba(255, 200, 95, 0.19), offset: 0 },
      { color: rgba(215, 125, 55, 0.055), offset: 0.24 },
      { color: rgba(0, 0, 0, 0), offset: 0.35 },
    ],
  },
  radialBefore: {
    centerXPct: 28,
    centerYPct: 12,
    glow: {
      opacityMax: 1,
      opacityMin: 0.92,
      periodMs: 14_000,
      reversePhase: false,
      scaleEnd: 1.1,
      scaleStart: 1,
      translateEndXPct: 6,
      translateEndYPct: 5,
      translateStartXPct: -4,
      translateStartYPct: -2,
    },
    stops: [
      { color: rgba(255, 220, 130, 0.21), offset: 0 },
      { color: rgba(245, 190, 95, 0.06), offset: 0.26 },
      { color: rgba(0, 0, 0, 0), offset: 0.42 },
    ],
  },
  lightBeams: defaultTripleCaveBeams(ACTIVE_SHAFT_STOPS),
}

/** Gold Rush / `.gold` shell overrides */
export const GOLD_RUSH_AMBIENT_SHELL_BACKGROUND: AmbientShellBackgroundConfig = {
  baseBottom: rgb(0x1a, 0x15, 0x0d),
  baseTop: rgb(0x14, 0x10, 0x08),
  radialAfter: {
    centerXPct: 76,
    centerYPct: 25,
    glow: {
      opacityMax: 1,
      opacityMin: 0.92,
      periodMs: 20_000,
      reversePhase: true,
      scaleEnd: 1.07,
      scaleStart: 1,
      translateEndXPct: 6,
      translateEndYPct: 5,
      translateStartXPct: -4,
      translateStartYPct: -2,
    },
    stops: [
      { color: rgba(255, 237, 179, 0.25), offset: 0 },
      { color: rgba(248, 214, 132, 0.1), offset: 0.25 },
      { color: rgba(0, 0, 0, 0), offset: 0.5 },
    ],
  },
  radialBefore: {
    centerXPct: 28,
    centerYPct: 12,
    glow: {
      opacityMax: 1,
      opacityMin: 0.92,
      periodMs: 14_000,
      reversePhase: false,
      scaleEnd: 1.07,
      scaleStart: 1,
      translateEndXPct: 6,
      translateEndYPct: 5,
      translateStartXPct: -4,
      translateStartYPct: -2,
    },
    stops: [
      { color: rgba(255, 227, 91, 0.24), offset: 0 },
      { color: rgba(255, 220, 140, 0.1), offset: 0.2 },
      { color: rgba(0, 0, 0, 0), offset: 0.4 },
    ],
  },
  lightBeams: defaultTripleCaveBeams(GOLD_SHAFT_STOPS),
}

/** Pressure shell overrides */
export const PRESSURE_AMBIENT_SHELL_BACKGROUND: AmbientShellBackgroundConfig = {
  baseBottom: rgb(0x1c, 0x10, 0x0f),
  baseTop: rgb(0x3a, 0x1c, 0x1a),
  radialAfter: {
    centerXPct: 76,
    centerYPct: 25,
    glow: {
      opacityMax: 1,
      opacityMin: 0.92,
      periodMs: 20_000,
      reversePhase: true,
      scaleEnd: 1.07,
      scaleStart: 1,
      translateEndXPct: 6,
      translateEndYPct: 5,
      translateStartXPct: -4,
      translateStartYPct: -2,
    },
    stops: [
      { color: rgba(96, 44, 42, 0.22), offset: 0 },
      { color: rgba(66, 38, 36, 0.08), offset: 0.25 },
      { color: rgba(0, 0, 0, 0), offset: 0.5 },
    ],
  },
  radialBefore: {
    centerXPct: 28,
    centerYPct: 12,
    glow: {
      opacityMax: 1,
      opacityMin: 0.92,
      periodMs: 14_000,
      reversePhase: false,
      scaleEnd: 1.07,
      scaleStart: 1,
      translateEndXPct: 6,
      translateEndYPct: 5,
      translateStartXPct: -4,
      translateStartYPct: -2,
    },
    stops: [
      { color: rgba(228, 118, 92, 0.2), offset: 0 },
      { color: rgba(176, 96, 80, 0.08), offset: 0.22 },
      { color: rgba(0, 0, 0, 0), offset: 0.4 },
    ],
  },
  lightBeams: defaultTripleCaveBeams(PRESSURE_SHAFT_STOPS),
}

/** End-run shell overrides */
export const END_AMBIENT_SHELL_BACKGROUND: AmbientShellBackgroundConfig = {
  baseBottom: rgb(0x4, 0x1, 0x1),
  baseTop: rgb(0x8, 0x2, 0x2),
  radialAfter: {
    centerXPct: 76,
    centerYPct: 25,
    glow: {
      opacityMax: 1,
      opacityMin: 0.92,
      periodMs: 20_000,
      reversePhase: true,
      scaleEnd: 1.07,
      scaleStart: 1,
      translateEndXPct: 6,
      translateEndYPct: 5,
      translateStartXPct: -4,
      translateStartYPct: -2,
    },
    stops: [
      { color: rgba(56, 24, 22, 0.5), offset: 0 },
      { color: rgba(26, 10, 5, 0.2), offset: 0.25 },
      { color: rgba(0, 0, 0, 0), offset: 0.5 },
    ],
  },
  radialBefore: {
    centerXPct: 28,
    centerYPct: 12,
    glow: {
      opacityMax: 1,
      opacityMin: 0.92,
      periodMs: 14_000,
      reversePhase: false,
      scaleEnd: 1.07,
      scaleStart: 1,
      translateEndXPct: 6,
      translateEndYPct: 5,
      translateStartXPct: -4,
      translateStartYPct: -2,
    },
    stops: [
      { color: rgba(56, 24, 22, 0.2), offset: 0 },
      { color: rgba(26, 10, 5, 0.1), offset: 0.25 },
      { color: rgba(0, 0, 0, 0), offset: 0.5 },
    ],
  },
  lightBeams: defaultTripleCaveBeams(END_SHAFT_STOPS),
}

function easeInOutSine(t: number): number {
  const c = clamp01(t)
  return -(Math.cos(Math.PI * c) - 1) / 2
}

/** One CSS keyframe lap: 0%→50%→100% with ease-in-out between endpoints (matches `ease-in-out` over full cycle). */
function cssGlowOscillation(phaseLinear01: number): number {
  let p = phaseLinear01 % 1
  if (p < 0) {
    p += 1
  }
  const u = p < 0.5 ? p * 2 : (1 - p) * 2
  return easeInOutSine(u)
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v))
}

function glowFactor(nowMs: number, glow: AmbientRadialGlowPhysics, reducedMotion: boolean): number {
  if (reducedMotion) {
    return 0
  }
  let phase = (nowMs % glow.periodMs) / glow.periodMs
  if (glow.reversePhase) {
    phase = 1 - phase
  }
  return cssGlowOscillation(phase)
}

/** Cached layout for a beam at a moment in time: maps local (t along shaft, u across) → world px. */
export type AmbientLightBeamWorldBasis = {
  glowK: number
  crackX: number
  crackY: number
  waveX: number
  waveY: number
  scale: number
  L: number
  dirX: number
  dirY: number
  perpX: number
  perpY: number
  hwTop: number
  hwBot: number
}

export function getAmbientLightBeamWorldBasis(
  beam: AmbientLightBeamConfig,
  w: number,
  h: number,
  nowMs: number,
  reducedMotion: boolean,
): AmbientLightBeamWorldBasis {
  const glowK = glowFactor(nowMs, beam.glow, reducedMotion)
  const txPct =
    beam.glow.translateStartXPct +
    (beam.glow.translateEndXPct - beam.glow.translateStartXPct) * glowK
  const tyPct =
    beam.glow.translateStartYPct +
    (beam.glow.translateEndYPct - beam.glow.translateStartYPct) * glowK
  const scale = beam.glow.scaleStart + (beam.glow.scaleEnd - beam.glow.scaleStart) * glowK
  const crackX = w / 2 + beam.crackXOffsetFromCenterPx
  const crackY = (beam.crackYPct / 100) * h
  const waveX = (txPct / 100) * w
  const waveY = (tyPct / 100) * h
  const L = (beam.reachPct / 100) * h
  const hwTop = beamHalfWidthCrackPx(beam)
  const hwBot = beamHalfWidthFootPx(beam)
  const rad = (beam.angleDeg * Math.PI) / 180
  const dirX = Math.sin(rad)
  const dirY = Math.cos(rad)
  return {
    crackX,
    crackY,
    dirX,
    dirY,
    glowK,
    hwBot,
    hwTop,
    L,
    perpX: -dirY,
    perpY: dirX,
    scale,
    waveX,
    waveY,
  }
}

/** `tAlong`: distance along beam from crack (0…L). `uAcross`: signed offset along perpendicular (half-width at t). */
export function ambientLightBeamWorldFromTU(
  basis: AmbientLightBeamWorldBasis,
  tAlong: number,
  uAcross: number,
): { x: number; y: number } {
  const t = Math.max(0, Math.min(basis.L, tAlong))
  const hwAtT =
    basis.hwTop + (basis.hwBot - basis.hwTop) * (basis.L > 0 ? t / basis.L : 0)
  const u = Math.max(-hwAtT, Math.min(hwAtT, uAcross))
  const lx = t * basis.dirX + u * basis.perpX
  const ly = t * basis.dirY + u * basis.perpY
  return {
    x: basis.waveX + basis.crackX + basis.scale * lx,
    y: basis.waveY + basis.crackY + basis.scale * ly,
  }
}

/** CSS `circle` radial default ending radius: distance from center to farthest corner of the box. */
export function ambientRadialExtentRadius(cx: number, cy: number, w: number, h: number): number {
  const corners: Array<[number, number]> = [
    [0, 0],
    [w, 0],
    [0, h],
    [w, h],
  ]
  let maxD = 0
  for (const [x, y] of corners) {
    const d = Math.hypot(x - cx, y - cy)
    if (d > maxD) {
      maxD = d
    }
  }
  return maxD
}

function rgbaCss(c: AmbientRgba): string {
  return `rgba(${Math.round(c.r)}, ${Math.round(c.g)}, ${Math.round(c.b)}, ${clamp01(c.a)})`
}

function drawRadialLayer(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  layer: AmbientRadialLayerConfig,
  nowMs: number,
  reducedMotion: boolean,
) {
  const cx = (layer.centerXPct / 100) * w
  const cy = (layer.centerYPct / 100) * h
  const R = ambientRadialExtentRadius(cx, cy, w, h)

  const k = glowFactor(nowMs, layer.glow, reducedMotion)
  const txPct =
    layer.glow.translateStartXPct +
    (layer.glow.translateEndXPct - layer.glow.translateStartXPct) * k
  const tyPct =
    layer.glow.translateStartYPct +
    (layer.glow.translateEndYPct - layer.glow.translateStartYPct) * k
  const scale =
    layer.glow.scaleStart + (layer.glow.scaleEnd - layer.glow.scaleStart) * k
  const opacity =
    layer.glow.opacityMin + (layer.glow.opacityMax - layer.glow.opacityMin) * k

  const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, R)
  for (const stop of layer.stops) {
    grd.addColorStop(stop.offset, rgbaCss(stop.color))
  }

  ctx.save()
  ctx.globalAlpha = opacity
  ctx.translate((txPct / 100) * w, (tyPct / 100) * h)
  ctx.translate(w / 2, h / 2)
  ctx.scale(scale, scale)
  ctx.translate(-w / 2, -h / 2)
  ctx.fillStyle = grd
  ctx.fillRect(0, 0, w, h)
  ctx.restore()
}

function drawLightBeam(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  beam: AmbientLightBeamConfig,
  nowMs: number,
  reducedMotion: boolean,
) {
  const basis = getAmbientLightBeamWorldBasis(beam, w, h, nowMs, reducedMotion)
  const opacityCurve =
    beam.glow.opacityMin + (beam.glow.opacityMax - beam.glow.opacityMin) * basis.glowK
  const opacity = opacityCurve * beam.depthOpacity
  const { L, dirX, dirY, perpX, perpY, hwTop, hwBot } = basis

  ctx.save()
  ctx.globalAlpha = clamp01(opacity)
  ctx.globalCompositeOperation = 'screen'
  ctx.filter = beam.edgeBlurPx > 0 ? `blur(${beam.edgeBlurPx}px)` : 'none'
  ctx.translate(basis.waveX, basis.waveY)
  ctx.translate(basis.crackX, basis.crackY)
  ctx.scale(basis.scale, basis.scale)
  const grd = ctx.createLinearGradient(0, 0, dirX * L, dirY * L)
  for (const stop of beam.stops) {
    grd.addColorStop(stop.offset, rgbaCss(stop.color))
  }

  ctx.beginPath()
  ctx.moveTo(perpX * -hwTop, perpY * -hwTop)
  ctx.lineTo(perpX * hwTop, perpY * hwTop)
  ctx.lineTo(dirX * L + perpX * hwBot, dirY * L + perpY * hwBot)
  ctx.lineTo(dirX * L + perpX * -hwBot, dirY * L + perpY * -hwBot)
  ctx.closePath()
  ctx.fillStyle = grd
  ctx.fill()
  ctx.restore()
}

export function drawAmbientShellBackground(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  config: AmbientShellBackgroundConfig,
  nowMs: number,
  reducedMotion: boolean,
) {
  const baseGrd = ctx.createLinearGradient(0, 0, 0, h)
  baseGrd.addColorStop(
    0,
    `rgb(${Math.round(config.baseTop.r)}, ${Math.round(config.baseTop.g)}, ${Math.round(config.baseTop.b)})`,
  )
  baseGrd.addColorStop(
    1,
    `rgb(${Math.round(config.baseBottom.r)}, ${Math.round(config.baseBottom.g)}, ${Math.round(config.baseBottom.b)})`,
  )
  ctx.globalAlpha = 1
  ctx.fillStyle = baseGrd
  ctx.fillRect(0, 0, w, h)

  drawRadialLayer(ctx, w, h, config.radialBefore, nowMs, reducedMotion)
  drawRadialLayer(ctx, w, h, config.radialAfter, nowMs, reducedMotion)

  if (config.lightBeams.length > 0) {
    const sorted = config.lightBeams.slice().sort((a, b) => a.depthOrder - b.depthOrder)
    for (const beam of sorted) {
      drawLightBeam(ctx, w, h, beam, nowMs, reducedMotion)
    }
  }

  ctx.globalAlpha = 1
  ctx.globalCompositeOperation = 'source-over'
}

export function mergeAmbientShellBackground(
  base: AmbientShellBackgroundConfig,
  patch?: Partial<AmbientShellBackgroundConfig>,
): AmbientShellBackgroundConfig {
  if (!patch) {
    return base
  }

  return {
    baseBottom: patch.baseBottom ?? base.baseBottom,
    baseTop: patch.baseTop ?? base.baseTop,
    radialAfter: patch.radialAfter
      ? { ...base.radialAfter, ...patch.radialAfter, stops: patch.radialAfter.stops ?? base.radialAfter.stops }
      : base.radialAfter,
    radialBefore: patch.radialBefore
      ? {
          ...base.radialBefore,
          ...patch.radialBefore,
          stops: patch.radialBefore.stops ?? base.radialBefore.stops,
        }
      : base.radialBefore,
    lightBeams: patch.lightBeams ?? base.lightBeams,
  }
}

function lerpRgb(a: AmbientRgb, b: AmbientRgb, u: number): AmbientRgb {
  return {
    b: a.b + (b.b - a.b) * u,
    g: a.g + (b.g - a.g) * u,
    r: a.r + (b.r - a.r) * u,
  }
}

function lerpRgba(a: AmbientRgba, b: AmbientRgba, u: number): AmbientRgba {
  return {
    a: a.a + (b.a - a.a) * u,
    b: a.b + (b.b - a.b) * u,
    g: a.g + (b.g - a.g) * u,
    r: a.r + (b.r - a.r) * u,
  }
}

function lerpRadialGlow(a: AmbientRadialGlowPhysics, b: AmbientRadialGlowPhysics, u: number): AmbientRadialGlowPhysics {
  return {
    opacityMax: a.opacityMax + (b.opacityMax - a.opacityMax) * u,
    opacityMin: a.opacityMin + (b.opacityMin - a.opacityMin) * u,
    periodMs: Math.round(a.periodMs + (b.periodMs - a.periodMs) * u),
    reversePhase: u >= 1 ? b.reversePhase : a.reversePhase,
    scaleEnd: a.scaleEnd + (b.scaleEnd - a.scaleEnd) * u,
    scaleStart: a.scaleStart + (b.scaleStart - a.scaleStart) * u,
    translateEndXPct:
      a.translateEndXPct + (b.translateEndXPct - a.translateEndXPct) * u,
    translateEndYPct:
      a.translateEndYPct + (b.translateEndYPct - a.translateEndYPct) * u,
    translateStartXPct:
      a.translateStartXPct + (b.translateStartXPct - a.translateStartXPct) * u,
    translateStartYPct:
      a.translateStartYPct + (b.translateStartYPct - a.translateStartYPct) * u,
  }
}

function lerpRadialLayer(
  a: AmbientRadialLayerConfig,
  b: AmbientRadialLayerConfig,
  u: number,
): AmbientRadialLayerConfig {
  const stopsLen = Math.max(a.stops.length, b.stops.length)
  const stops: AmbientRadialStop[] = []
  for (let index = 0; index < stopsLen; index += 1) {
    const sa = a.stops[Math.min(index, a.stops.length - 1)]!
    const sb = b.stops[Math.min(index, b.stops.length - 1)]!
    stops.push({
      color: lerpRgba(sa.color, sb.color, u),
      offset: sa.offset + (sb.offset - sa.offset) * u,
    })
  }

  return {
    centerXPct: a.centerXPct + (b.centerXPct - a.centerXPct) * u,
    centerYPct: a.centerYPct + (b.centerYPct - a.centerYPct) * u,
    glow: lerpRadialGlow(a.glow, b.glow, u),
    stops,
  }
}

function lerpBeamGradientStops(
  a: AmbientBeamGradientStop[],
  b: AmbientBeamGradientStop[],
  u: number,
): AmbientBeamGradientStop[] {
  const stopsLen = Math.max(a.length, b.length)
  const stops: AmbientBeamGradientStop[] = []
  for (let index = 0; index < stopsLen; index += 1) {
    const sa = a[Math.min(index, a.length - 1)]!
    const sb = b[Math.min(index, b.length - 1)]!
    stops.push({
      color: lerpRgba(sa.color, sb.color, u),
      offset: sa.offset + (sb.offset - sa.offset) * u,
    })
  }
  return stops
}

function lerpLightBeam(a: AmbientLightBeamConfig, b: AmbientLightBeamConfig, u: number): AmbientLightBeamConfig {
  return {
    angleDeg: a.angleDeg + (b.angleDeg - a.angleDeg) * u,
    crackXOffsetFromCenterPx:
      a.crackXOffsetFromCenterPx + (b.crackXOffsetFromCenterPx - a.crackXOffsetFromCenterPx) * u,
    crackYPct: a.crackYPct + (b.crackYPct - a.crackYPct) * u,
    depthOpacity: a.depthOpacity + (b.depthOpacity - a.depthOpacity) * u,
    depthOrder: u >= 1 ? b.depthOrder : a.depthOrder,
    edgeBlurPx: a.edgeBlurPx + (b.edgeBlurPx - a.edgeBlurPx) * u,
    glow: lerpRadialGlow(a.glow, b.glow, u),
    halfWidthCrackPct: a.halfWidthCrackPct + (b.halfWidthCrackPct - a.halfWidthCrackPct) * u,
    halfWidthFootPct: a.halfWidthFootPct + (b.halfWidthFootPct - a.halfWidthFootPct) * u,
    reachPct: a.reachPct + (b.reachPct - a.reachPct) * u,
    stops: lerpBeamGradientStops(a.stops, b.stops, u),
  }
}

function lerpLightBeams(
  from: AmbientLightBeamConfig[],
  to: AmbientLightBeamConfig[],
  u: number,
): AmbientLightBeamConfig[] {
  const n = Math.max(from.length, to.length)
  const out: AmbientLightBeamConfig[] = []
  for (let index = 0; index < n; index += 1) {
    const fa = from[index]
    const tb = to[index]
    if (fa && tb) {
      out.push(lerpLightBeam(fa, tb, u))
    } else if (fa) {
      out.push(fa)
    } else if (tb) {
      out.push(tb)
    }
  }
  return out
}

export function lerpAmbientShellBackground(
  from: AmbientShellBackgroundConfig,
  to: AmbientShellBackgroundConfig,
  uLinear: number,
): AmbientShellBackgroundConfig {
  const u = clamp01(uLinear)
  return {
    baseBottom: lerpRgb(from.baseBottom, to.baseBottom, u),
    baseTop: lerpRgb(from.baseTop, to.baseTop, u),
    radialAfter: lerpRadialLayer(from.radialAfter, to.radialAfter, u),
    radialBefore: lerpRadialLayer(from.radialBefore, to.radialBefore, u),
    lightBeams: lerpLightBeams(from.lightBeams, to.lightBeams, u),
  }
}

export function ambientShellBackgroundsEqual(
  a: AmbientShellBackgroundConfig,
  b: AmbientShellBackgroundConfig,
): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    return false
  }
}
